/**
 * Organisation timezone display helpers.
 * SQL stores Windows TimeZoneId (AT TIME ZONE). WhatsApp CreatedOn is UTC
 * (SYSUTCDATETIME). Format with IANA so the UI follows the org, not the browser.
 */

export const DEFAULT_ORG_TIME_ZONE_ID = "GMT Standard Time";

const WINDOWS_TO_IANA = {
  "GMT Standard Time": "Europe/London",
  "UTC": "UTC",
  "Arabian Standard Time": "Asia/Dubai",
  "India Standard Time": "Asia/Kolkata",
  "W. Europe Standard Time": "Europe/Berlin",
  "Romance Standard Time": "Europe/Paris",
  "Eastern Standard Time": "America/New_York",
  "Central Standard Time": "America/Chicago",
  "Pacific Standard Time": "America/Los_Angeles",
  "AUS Eastern Standard Time": "Australia/Sydney",
  "Singapore Standard Time": "Asia/Singapore",
  "South Africa Standard Time": "Africa/Johannesburg",
};

export function windowsTimeZoneToIana(windowsId) {
  const key = String(windowsId || "").trim();
  if (!key) return WINDOWS_TO_IANA[DEFAULT_ORG_TIME_ZONE_ID];
  if (WINDOWS_TO_IANA[key]) return WINDOWS_TO_IANA[key];
  if (key.includes("/")) return key;
  return WINDOWS_TO_IANA[DEFAULT_ORG_TIME_ZONE_ID];
}

/** Parse API datetime as a real instant. Naive ISO (no Z) is UTC. */
export function parseUtcInstant(value) {
  if (value == null || value === "") return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    const fromNumber = new Date(value);
    return Number.isNaN(fromNumber.getTime()) ? null : fromNumber;
  }
  const str = String(value).trim();
  if (!str) return null;

  const msAjax = str.match(/\/Date\((-?\d+)(?:[+-]\d+)?\)\//);
  if (msAjax) {
    const fromAjax = new Date(Number(msAjax[1]));
    return Number.isNaN(fromAjax.getTime()) ? null : fromAjax;
  }

  if (/[zZ]|[+-]\d{2}:?\d{2}$/.test(str)) {
    const fromOffset = new Date(str);
    return Number.isNaN(fromOffset.getTime()) ? null : fromOffset;
  }

  const iso = str.match(
    /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?/,
  );
  if (iso) {
    return new Date(
      Date.UTC(
        Number(iso[1]),
        Number(iso[2]) - 1,
        Number(iso[3]),
        Number(iso[4]),
        Number(iso[5]),
        Number(iso[6] || 0),
      ),
    );
  }

  const fallback = new Date(str);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
}

function orgYmd(date, iana) {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: iana,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);
  const pick = (type) => parts.find((p) => p.type === type)?.value || "";
  return `${pick("year")}-${pick("month")}-${pick("day")}`;
}

function orgDayDiff(fromDate, toDate, iana) {
  const fromKey = orgYmd(fromDate, iana);
  const toKey = orgYmd(toDate, iana);
  const fromMs = Date.parse(`${fromKey}T12:00:00Z`);
  const toMs = Date.parse(`${toKey}T12:00:00Z`);
  return Math.round((toMs - fromMs) / 86400000);
}

function formatClock(date, iana) {
  const raw = new Intl.DateTimeFormat("en-GB", {
    timeZone: iana,
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  }).format(date);
  return raw.replace(/\s*(am|pm)\s*$/i, (_, mer) => ` ${mer.toUpperCase()}`);
}

export function formatOrgClockTime(value, timeZoneId) {
  const date = parseUtcInstant(value);
  if (!date) return "";
  return formatClock(date, windowsTimeZoneToIana(timeZoneId));
}

export function formatOrgWhenShort(value, timeZoneId) {
  const date = parseUtcInstant(value);
  if (!date) return "";
  const iana = windowsTimeZoneToIana(timeZoneId);
  if (orgYmd(date, iana) === orgYmd(new Date(), iana)) {
    return formatClock(date, iana);
  }
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: iana,
    day: "2-digit",
    month: "short",
  }).format(date);
}

export function formatOrgDayChip(value, timeZoneId) {
  const date = parseUtcInstant(value);
  if (!date) return "";
  const iana = windowsTimeZoneToIana(timeZoneId);
  const diff = orgDayDiff(date, new Date(), iana);
  if (diff === 0) return "Today";
  if (diff === 1) return "Yesterday";
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: iana,
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
}

export function withinOrgDateFilter(value, dateFilter, timeZoneId) {
  if (dateFilter === "all" || !value) return true;
  const date = parseUtcInstant(value);
  if (!date) return true;
  const iana = windowsTimeZoneToIana(timeZoneId);
  const diff = orgDayDiff(date, new Date(), iana);
  if (dateFilter === "today") return diff === 0;
  const days = dateFilter === "7d" ? 7 : 30;
  return diff >= 0 && diff < days;
}

/** Match LastMessageOn against shared DateRangeFilter (All Time / preset / custom). */
export function withinOrgDateRange(value, dateRange, timeZoneId) {
  const preset = String(dateRange?.preset || dateRange?.dateRange || "All Time").trim();
  if (!preset || /all time/i.test(preset)) return true;
  const startIso = String(dateRange?.startDate || dateRange?.dateFrom || "").trim();
  const endIso = String(dateRange?.endDate || dateRange?.dateTo || "").trim();
  if (!startIso && !endIso) return true;
  const date = parseUtcInstant(value);
  if (!date) return false;
  const ymd = orgYmd(date, windowsTimeZoneToIana(timeZoneId));
  if (startIso && ymd < startIso) return false;
  if (endIso && ymd > endIso) return false;
  return true;
}

/**
 * AccessUntil / Visible until are org wall-clock datetimes (same as
 * GetZoneBasedDateTime in SQL) — not UTC and not browser-local.
 * Returns 'yyyy-MM-dd HH:mm:ss' for lexicographic compare, or "".
 */
export function toOrgWallClockComparable(value) {
  const text = String(value ?? "").trim();
  if (!text) return "";

  const iso = text.match(
    /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/,
  );
  if (iso) {
    return `${iso[1]}-${iso[2]}-${iso[3]} ${iso[4]}:${iso[5]}:${iso[6] || "00"}`;
  }

  const dmy = text.match(
    /^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{4})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?)?/,
  );
  if (dmy) {
    const dd = String(dmy[1]).padStart(2, "0");
    const mm = String(dmy[2]).padStart(2, "0");
    const yyyy = dmy[3];
    const hh = String(dmy[4] || "0").padStart(2, "0");
    const min = String(dmy[5] || "0").padStart(2, "0");
    const ss = String(dmy[6] || "0").padStart(2, "0");
    return `${yyyy}-${mm}-${dd} ${hh}:${min}:${ss}`;
  }

  // /Date(ms)/ — use UTC parts; serializers often emit Unspecified as UTC ms of wall clock.
  const msAjax = text.match(/\/Date\((-?\d+)(?:[+-]\d+)?\)\//);
  if (msAjax) {
    const d = new Date(Number(msAjax[1]));
    if (Number.isNaN(d.getTime())) return "";
    const yyyy = d.getUTCFullYear();
    const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
    const dd = String(d.getUTCDate()).padStart(2, "0");
    const hh = String(d.getUTCHours()).padStart(2, "0");
    const min = String(d.getUTCMinutes()).padStart(2, "0");
    const ss = String(d.getUTCSeconds()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd} ${hh}:${min}:${ss}`;
  }

  return "";
}

/** Current instant as org wall-clock 'yyyy-MM-dd HH:mm:ss'. */
export function formatNowOrgWallClockComparable(timeZoneId) {
  const iana = windowsTimeZoneToIana(timeZoneId);
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: iana,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(new Date());
  const pick = (type) => parts.find((p) => p.type === type)?.value || "00";
  let hour = pick("hour");
  if (hour === "24") hour = "00";
  return `${pick("year")}-${pick("month")}-${pick("day")} ${hour}:${pick("minute")}:${pick("second")}`;
}

export function isOrgWallClockPast(value, timeZoneId) {
  const until = toOrgWallClockComparable(value);
  if (!until) return false;
  return until < formatNowOrgWallClockComparable(timeZoneId);
}

/** Display AccessUntil as dd/MM/yyyy hh:mm tt (12-hour). Whole day → date only. */
export function formatOrgWallClockDisplay(value) {
  const until = toOrgWallClockComparable(value);
  if (!until) return "";
  const [datePart, timePart] = until.split(" ");
  const [yyyy, mm, dd] = datePart.split("-");
  const [hhRaw, min] = (timePart || "00:00:00").split(":");
  const hour24 = Number(hhRaw);
  const dateOut = `${dd}/${mm}/${yyyy}`;
  // End-of-day whole-day Visible until → date only.
  if (hhRaw === "23" && min === "59") {
    return dateOut;
  }
  // Midnight whole-day token → date only.
  if (hhRaw === "00" && min === "00") {
    return dateOut;
  }
  const ampm = hour24 >= 12 ? "PM" : "AM";
  let hour12 = hour24 % 12;
  if (hour12 === 0) hour12 = 12;
  const hour = String(hour12).padStart(2, "0");
  return `${dateOut} ${hour}:${min} ${ampm}`;
}
